import { useState, useCallback } from 'react';
import { BlockFormState } from './types';

export const useRoles = (kedo: any) => {
    const [availableRoles, setAvailableRoles] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchRoles = useCallback(async () => {
        const environment = kedo.env().getEnvironment();
        setLoading(true);

        try {
            const response = await kedo.api().get(kedo.api().getRoleEndpoint(), {
                params: { environment: environment.id, limit: 200 },
            });

            setAvailableRoles(response.data.results);
        } catch (error) {
            // Handle error
        } finally {
            setLoading(false);
        }
    }, [kedo]);

    return { availableRoles, loading, fetchRoles };
};

export const useDefDossiers = (kedo: any) => {
    const [defDossiers, setDefDossiers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchDefDossiers = useCallback(async () => {
        const api = kedo.api();
        setLoading(true);
        try {
            const response = await api.getCached(api.getDefDossierEndpoint(), {
                params: { environment: kedo.env().getEnvironment().id, limit: 200 },
            });
            setDefDossiers(response.data.results);
        } catch (error) {
            // Handle error
        } finally {
            setLoading(false);
        }
    }, [kedo]);

    return { defDossiers, loading, fetchDefDossiers };
};

export const useCustomViews = (kedo: any, defDossier: number | null) => {
    const [views, setViews] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchCustomViews = useCallback(async () => {
        if (!defDossier) return;
        const api = kedo.api();
        setLoading(true);
        try {
            const response = await api.get(api.getDisplayCustomViewEndpoint(), {
                params: { environment: kedo.env().getEnvironment().id, defDossier, limit: 200 },
            });
            setViews(response.data.results);
        } catch (error) {
            // Handle error
        } finally {
            setLoading(false);
        }
    }, [kedo, defDossier]);

    return { views, loading, fetchCustomViews };
};

export const useQueries = (kedo: any, defDossier: number | null) => {
    const [dossierQueries, setDossierQueries] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchQueries = useCallback(async () => {
        if (!defDossier) return;
        const api = kedo.api();
        setLoading(true);
        try {
            const response = await api.get(api.getDossierQueryEndpoint(), {
                params: { environment: kedo.env().getEnvironment().id, defDossier, limit: 200 },
            });
            setDossierQueries(response.data.results);
        } catch (error) {
            // Handle error
        } finally {
            setLoading(false);
        }
    }, [kedo, defDossier]);

    return { dossierQueries, loading, fetchQueries };
};

export const useBlockFormState = (initialItem?: any) => {
    const getInitialSetting = (key: string, defaultValue: any) =>
        initialItem && initialItem.settings && initialItem.settings[key] !== undefined
            ? initialItem.settings[key]
            : defaultValue;

    const initialItemState: any = initialItem
        ? {
              id: initialItem.id,
              translations: initialItem.translations,
              defDossier: initialItem.def_dossier_id,
              dossierQuery: initialItem.dossier_query_id,
              roles: initialItem?.roles ? initialItem?.roles.map((r: any) => r.role_id) : [],
              col: initialItem.col,
              rank: initialItem.rank,
              dashboard: initialItem.dashboard,
              type: initialItem.type,
              settings: initialItem.settings || {},
              parent_id: initialItem.parent_id
          }
        : {
              type: 'dashboard',
              col: 0,
              rank: 0,
              defDossier: null,
              translations: [],
              roles: [],
              dashboard: true,
              settings: {},
          };

    const [state, setState] = useState<BlockFormState>({
        parents: [],
        defDossiers: [],
        dossierQuery: false,
        countLinkDefDossier: null,
        countLinkDefDossierSec: null,
        originLinkDefDossier: null,
        dossierQueries: [],
        delete: false,
        errors: false,
        views: [],
        availableRoles: [],
        parent: initialItem ? initialItem.parent_id : null,
        item: {
            ...initialItemState,
            settings: {
                sort: getInitialSetting('sort', null),
                view_name: getInitialSetting('view_name', 'list'),
                authKey: getInitialSetting('authKey', null),
                authToken: getInitialSetting('authToken', null),
                dashboardId: getInitialSetting('dashboardId', null),
                show_add: getInitialSetting('show_add', false),
                tabQueries: getInitialSetting('tab_queries', false),
                template: getInitialSetting('template', null),
                templateOptions: getInitialSetting('templateOptions', {}),
                pie_charts: getInitialSetting('pie_charts', null),
                list_field: getInitialSetting('list_field', null),
                count_list_field: getInitialSetting('count_list_field', null),
                count_link_field: getInitialSetting('count_link_field', null),
                count_link_field_sec: getInitialSetting('count_link_field_sec', null),
                link_field: getInitialSetting('link_field', null),
                origin_link_field: getInitialSetting('origin_link_field', null),
                scheme: getInitialSetting('scheme', null),
                date_field: getInitialSetting('date_field', null),
                sum_field: getInitialSetting('sum_field', null),
                limit: getInitialSetting('limit', 10),
                carousel: getInitialSetting('carousel', false),
                use_creation_date: getInitialSetting('use_creation_date', false),
                group_format: getInitialSetting('group_format', null),
                date_format: getInitialSetting('date_format', null),
                chart_format: getInitialSetting('chart_format', null),
                chart_layout: getInitialSetting('chart_layout', null),
                chart_identifier: getInitialSetting('chart_identifier', null),
                table_layout: getInitialSetting('table_layout', null),
                reverse_axis: getInitialSetting('reverse_axis', null),
                show_y_scroll: getInitialSetting('show_y_scroll', null),
                enable_labels: getInitialSetting('enable_labels', null),
                enable_grid: getInitialSetting('enable_grid', null),
                origin_display_field: getInitialSetting('origin_display_field', null),
                link_display_field: getInitialSetting('link_display_field', null),
                legend_direction: getInitialSetting('legend_direction', null),
                layout: getInitialSetting('layout', 'table'),
                period: getInitialSetting('period', null),
            }
        },
        submitting: false,
        loading: false,
        showAddView: false,
    });

    const handleChangeItem = useCallback((key: string, val: any) => {
        setState((prevState) => ({ ...prevState, item: { ...prevState.item, [key]: val } }));
    }, []);

    const handleChangeSettings = useCallback((key: string, val: any) => {
        setState((prevState) => ({ ...prevState, item: { ...prevState.item, settings: { ...prevState.item.settings, [key]: val } } }));
    }, []);

    const handleChangeState = useCallback((key: keyof BlockFormState, val: any) => {
        setState((prevState) => ({ ...prevState, [key]: val }));
    }, []);

    const handleTemplateOptionsChange = useCallback((fieldName: string, fieldValue: any) => {
        setState((prevState) => ({
            ...prevState,
            item: {
                ...prevState.item,
                settings: {
                    ...prevState.item.settings,
                    templateOptions: { ...prevState.item.settings.templateOptions, [fieldName]: fieldValue }
                }
            }
        }));
    }, []);

    return {
        state,
        setState,
        handleChangeItem,
        handleChangeSettings,
        handleChangeState,
        handleTemplateOptionsChange,
    };
};